import { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { TrendingUp, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["Senior Manager","Director","Vice President","Head of X","Chief of Staff","Chief Operating Officer","Chief Technology Officer","Chief Financial Officer","Chief Executive Officer"];

export default function Advance() {
  const [current, setCurrent] = useState("Director");
  const [target, setTarget] = useState("Chief Operating Officer");
  const [years, setYears] = useState(10);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const gen = async () => {
    setBusy(true); setData(null);
    try {
      const { data } = await api.post("/advance/ladder", {
        current_role: current, target_role: target, years_experience: parseInt(years || 0),
      });
      setData(data);
    } catch { toast.error("Ladder unavailable"); }
    finally { setBusy(false); }
  };

  const plan = data?.plan;

  return (
    <div className="space-y-10" data-testid="advance-page">
      <div>
        <div className="label-caps mb-2 flex items-center gap-1.5"><TrendingUp className="h-3 w-3 text-fuchsia-400" />Pillar · Advance</div>
        <h1 className="text-3xl sm:text-5xl tracking-tight font-semibold">Chart your climb.</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          A grounded, stage-by-stage ladder from where you are to where you want to be.
        </p>
      </div>

      <Card className="p-6 md:p-8">
        <div className="grid md:grid-cols-4 gap-4">
          <div>
            <Label>From</Label>
            <Select value={current} onValueChange={setCurrent}>
              <SelectTrigger data-testid="advance-from" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>To</Label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger data-testid="advance-to" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Years of experience</Label>
            <Input type="number" value={years} onChange={(e) => setYears(e.target.value)} data-testid="advance-years" className="mt-1.5" />
          </div>
          <div className="flex items-end">
            <Button onClick={gen} disabled={busy} className="rounded-full w-full" data-testid="advance-generate">
              <Sparkles className="h-4 w-4 mr-1" />{busy ? "Planning…" : "Chart ladder"}
            </Button>
          </div>
        </div>
      </Card>

      {plan && (plan.stages?.length > 0) && (
        <div data-testid="advance-plan">
          <div className="flex items-center justify-between mb-5">
            <div className="label-caps">Your ladder · {plan.overall_timeline_years ? `${plan.overall_timeline_years} years` : "flexible timeline"}</div>
          </div>
          <div className="grid gap-4">
            {plan.stages.map((s, i) => (
              <Card key={i} className="p-6">
                <div className="flex items-start gap-4">
                  <div className="font-mono text-3xl text-primary tabular-nums shrink-0">{String(i + 1).padStart(2, "0")}</div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <div className="text-lg font-medium">{s.name}</div>
                      {s.duration_months && <Badge variant="secondary" className="font-mono text-xs">{s.duration_months} mo</Badge>}
                    </div>
                    {s.focus?.length > 0 && (
                      <div className="mt-3">
                        <div className="label-caps mb-1">Focus</div>
                        <ul className="text-sm space-y-0.5">{s.focus.map((f, j) => <li key={j} className="flex gap-2"><span className="text-primary">·</span>{f}</li>)}</ul>
                      </div>
                    )}
                    {s.evidence_to_build?.length > 0 && (
                      <div className="mt-3">
                        <div className="label-caps mb-1">Evidence to build</div>
                        <ul className="text-sm space-y-0.5">{s.evidence_to_build.map((f, j) => <li key={j} className="flex gap-2"><span className="text-emerald-400">+</span>{f}</li>)}</ul>
                      </div>
                    )}
                    {s.risks?.length > 0 && (
                      <div className="mt-3">
                        <div className="label-caps mb-1">Risks</div>
                        <ul className="text-sm space-y-0.5 text-muted-foreground">{s.risks.map((f, j) => <li key={j}>· {f}</li>)}</ul>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
          {plan.key_decisions?.length > 0 && (
            <Card className="p-6 mt-4">
              <div className="label-caps mb-2">Key decisions along the way</div>
              <ul className="text-sm space-y-1">{plan.key_decisions.map((d, i) => <li key={i} className="flex gap-2"><span className="text-primary font-mono">→</span>{d}</li>)}</ul>
              {plan.coach_prompt && <p className="mt-4 text-sm italic text-muted-foreground">"{plan.coach_prompt}"</p>}
            </Card>
          )}
          {data.citations?.length > 0 && (
            <Card className="p-5 mt-4">
              <div className="label-caps mb-2">Grounded in</div>
              <div className="space-y-1">
                {data.citations.map((c) => (
                  <Link key={c.n} to={`/app/learn/chapter/${c.chapter_id}`}
                    className="block text-xs font-mono text-muted-foreground hover:text-foreground">
                    [{c.n}] {c.title} <ArrowRight className="inline h-3 w-3 ml-1" />
                  </Link>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
